"use client"

import { Button } from "@mdxui/primitives"
import { motion, useReducedMotion } from "framer-motion"
import type { ReactNode } from "react"
import { ArrowUp, Check, Sparkles } from "lucide-react"
import Markdown from "react-markdown"
import { cn } from "../../lib/cn"
import { BEHIND_NAVBAR_CLASSES } from "../../lib/constants"
import {
  gridContainerVariants,
  listItemVariants,
  reducedMotionVariants,
  sectionVariants,
  slideFromLeftVariants,
  slideFromRightVariants,
} from "../../lib/motion"

export interface HeroSplitTableProps {
  /** Main heading */
  title?: string
  /** Supporting text below the heading */
  subtitle?: string
  /** Feature bullet items with check icons */
  features?: string[]
  /** Primary action button */
  primaryButton?: { text: string; url: string }
  /** Secondary action button */
  secondaryButton?: { text: string; url: string }
  /** Button position relative to features list */
  buttonPosition?: "above" | "below"
  /** Column header labels */
  columns?: string[]
  /** Row data — each inner array maps to columns */
  rows?: string[][]
  /** Summary text for the floating pill at the bottom */
  summary?: string
  /** Optional background pattern (e.g., GradientBlur, GridDashed, etc.) */
  background?: ReactNode
  /** Extend behind sticky navbar. @default true */
  behindNavbar?: boolean
  className?: string
}

const HeroSplitTable = ({
  title = "Build Beautiful Interfaces with MDXUI",
  subtitle = "Type-safe components for building Sites and Apps with MDX. Production-ready and fully accessible.",
  features = [
    "Type-safe components",
    "Dark mode support",
    "Fully accessible",
    "Production ready",
  ],
  primaryButton = { text: "Get Started", url: "#" },
  secondaryButton,
  buttonPosition = "above",
  columns = [],
  rows = [],
  summary,
  background,
  behindNavbar = true,
  className,
}: HeroSplitTableProps) => {
  const prefersReducedMotion = useReducedMotion()

  const contentAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : slideFromLeftVariants
  const tableAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : slideFromRightVariants
  const itemAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : listItemVariants

  return (
    <div
      className={cn(
        "relative overflow-hidden",
        behindNavbar && BEHIND_NAVBAR_CLASSES,
        className,
      )}
    >
      {background && <div className="absolute inset-0">{background}</div>}
      <motion.section
        className="relative z-10 overflow-x-hidden py-16 sm:py-24 lg:py-32"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-0">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            {/* Content - Left */}
            <motion.div
              className="w-full min-w-0 max-w-full"
              variants={contentAnimation}
            >
              <h1 className="mb-5 font-semibold text-4xl lg:text-5xl">
                {title}
              </h1>
              <div
                className={cn(
                  "prose prose-base max-w-none text-muted-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none",
                  buttonPosition === "above" ? "mb-8" : "mb-8 lg:mb-12",
                )}
              >
                <Markdown>{subtitle}</Markdown>
              </div>
              {/* Button group - above */}
              {buttonPosition === "above" &&
                (primaryButton || secondaryButton) && (
                  <motion.div
                    className="mb-8 flex max-w-full flex-col gap-3 sm:flex-row lg:mb-12"
                    variants={itemAnimation}
                  >
                    {primaryButton && (
                      <Button asChild className="w-full sm:w-auto">
                        <a href={primaryButton.url}>{primaryButton.text}</a>
                      </Button>
                    )}
                    {secondaryButton && (
                      <Button
                        variant="outline"
                        asChild
                        className="w-full sm:w-auto"
                      >
                        <a href={secondaryButton.url}>{secondaryButton.text}</a>
                      </Button>
                    )}
                  </motion.div>
                )}
              <motion.ul
                className="grid max-w-xl gap-4 sm:grid-cols-2"
                variants={gridContainerVariants(0.05, 0.1)}
              >
                {features.map((feature, index) => (
                  <motion.li
                    key={String(index)}
                    className="flex min-w-0 items-center gap-2"
                    variants={itemAnimation}
                  >
                    <Check className="size-5 shrink-0 text-primary" />
                    <p className="break-words text-sm font-medium">{feature}</p>
                  </motion.li>
                ))}
              </motion.ul>
              {/* Button group - below */}
              {buttonPosition === "below" &&
                (primaryButton || secondaryButton) && (
                  <motion.div
                    className="mt-8 flex max-w-full flex-col gap-3 sm:flex-row lg:mt-12"
                    variants={itemAnimation}
                  >
                    {primaryButton && (
                      <Button asChild className="w-full sm:w-auto">
                        <a href={primaryButton.url}>{primaryButton.text}</a>
                      </Button>
                    )}
                    {secondaryButton && (
                      <Button
                        variant="outline"
                        asChild
                        className="w-full sm:w-auto"
                      >
                        <a href={secondaryButton.url}>{secondaryButton.text}</a>
                      </Button>
                    )}
                  </motion.div>
                )}
            </motion.div>

            {/* Table - Right, contained card */}
            {columns.length > 0 && (
              <motion.div className="relative min-w-0" variants={tableAnimation}>
                <div className="bg-card/90 ring-border relative w-full overflow-hidden rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-transparent pb-12 shadow-2xl shadow-black/10 ring-1">
                    {summary && (
                      <div className="max-w-xs absolute inset-x-0 bottom-4 z-10 mx-auto">
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-400 via-teal-400 to-purple-400 opacity-15 blur" />
                        <div className="shadow-black/6.5 ring-border bg-card relative flex items-center gap-2 rounded-full p-1 shadow-md ring-1">
                          <div className="bg-card ring-border flex size-6 shrink-0 rounded-full ring-1 *:m-auto">
                            <Sparkles className="size-3" />
                          </div>
                          <div className="line-clamp-1 text-[13px]">
                            {summary}
                          </div>
                          <ArrowUp className="mr-2 size-3 shrink-0 opacity-65" />
                        </div>
                      </div>
                    )}

                    <div className="overflow-hidden rounded-t-[var(--radius-xl,calc(var(--radius)*1.5))]">
                    <div className="mask-b-from-55% overflow-x-auto">
                      <table className="w-max min-w-full border-collapse">
                        <thead className="dark:bg-background bg-foreground/5">
                          <tr className="*:whitespace-nowrap *:border *:border-border *:px-3 *:py-1.5 *:text-left *:text-sm *:font-medium">
                            {columns.map((col, i) => (
                              <th key={i}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="text-sm">
                          {rows.map((row, rowIndex) => (
                            <tr key={rowIndex} className="text-foreground/75 *:whitespace-nowrap *:border *:border-border *:p-2 *:px-3">
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex}>
                                  {cellIndex === 0 ? (
                                    <span className="text-foreground">{cell}</span>
                                  ) : (
                                    cell
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </motion.section>
    </div>
  )
}

export { HeroSplitTable }
